import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import CardSurface from '../../components/card-surface/CardSurface'
import Button from '../../components/button/Button'
import TextField from '../../components/text-field/TextField'
import ChoiceTileGroup from '../../components/choice-tile/ChoiceTileGroup'
import {
  getRenewalOffer,
  respondToRenewalOffer,
} from '../../_data/renewal-offer'
import {
  PublicRenewalOffer,
  RenewalOfferDeclineReason,
  RenewalOfferDecision,
} from '../../_types/renewal-offer'
import {
  DECLINE_REASON_OPTIONS,
  describeClosedOffer,
  describeHome,
  describeSender,
  formatLeaseDate,
  formatRent,
} from './helpers'
import {
  ActionRow,
  AgreeText,
  Body,
  BrandLogo,
  BrandName,
  BrandRow,
  CardWrap,
  Content,
  DetailLabel,
  DetailList,
  DetailValue,
  ErrorText,
  PageRoot,
  TermsText,
  Title,
} from './styled'
import { RenewalOfferPageState } from './types'

/**
 * Public landing for `#/renewal-offer?token=...`, the link a renter is emailed
 * and texted with a lease renewal offer. Works signed out on purpose: a renter
 * synced from their landlord's property-management system has no QuickPass
 * account, and the token in the link is the only credential. Accepting is a
 * typed-name e-signature; declining can say why, which is what lets the next
 * offer answer the actual objection.
 *
 * @returns {JSX.Element}
 */
const RenewalOfferPage = (): JSX.Element => {
  const [searchParams] = useSearchParams()
  const token = useMemo<string>(
    () => searchParams.get('token') ?? '',
    [searchParams],
  )

  const [pageState, setPageState] = useState<RenewalOfferPageState>('loading')
  const [offer, setOffer] = useState<PublicRenewalOffer | null>(null)
  const [signerName, setSignerName] = useState<string>('')
  const [declineReason, setDeclineReason] =
    useState<RenewalOfferDeclineReason | null>(null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    if (token === '') {
      setErrorMessage('This link is missing part of its address.')
      setPageState('invalid')
      return
    }

    void getRenewalOffer(token).then(result => {
      if (result.offer === null) {
        setErrorMessage(result.errorMessage)
        setPageState('invalid')
        return
      }

      setOffer(result.offer)
      setPageState(result.offer.status === 'sent' ? 'review' : 'closed')
    })
  }, [token])

  const submit = useCallback(
    async (decision: RenewalOfferDecision) => {
      setIsSubmitting(true)
      setErrorMessage('')

      const result = await respondToRenewalOffer({
        token,
        decision,
        signerName: decision === 'accept' ? signerName.trim() : '',
        declineReason: decision === 'decline' ? declineReason : null,
      })

      setIsSubmitting(false)

      if (result.offer === null) {
        setErrorMessage(result.errorMessage)
        return
      }

      setOffer(result.offer)
      setPageState(decision === 'accept' ? 'accepted' : 'declined')
    },
    [token, signerName, declineReason],
  )

  if (pageState === 'loading') {
    return (
      <PageRoot>
        <CardWrap>
          <CardSurface padding="loose">
            <Content>
              <Title>Loading your offer</Title>
            </Content>
          </CardSurface>
        </CardWrap>
      </PageRoot>
    )
  }

  if (pageState === 'invalid' || offer === null) {
    return (
      <PageRoot>
        <CardWrap>
          <CardSurface padding="loose">
            <Content>
              <Title>We could not open this offer</Title>
              <Body>
                {errorMessage === '' ? 'That link is not valid.' : errorMessage}{' '}
                Check that you opened the full link from your email or text,
                or contact your property manager.
              </Body>
            </Content>
          </CardSurface>
        </CardWrap>
      </PageRoot>
    )
  }

  const sender = describeSender(offer)
  const home = describeHome(offer)
  const rent = formatRent(offer.newRentCents, offer.currency)
  const start = formatLeaseDate(offer.newLeaseStartDate)
  const end = formatLeaseDate(offer.newLeaseEndDate)
  const greeting =
    offer.renterFirstName === '' ? 'Hi there,' : `Hi ${offer.renterFirstName},`

  const brand = (
    <BrandRow>
      {offer.companyLogoUrl !== '' ? (
        <BrandLogo src={offer.companyLogoUrl} alt="" />
      ) : null}
      <BrandName>{sender}</BrandName>
    </BrandRow>
  )

  if (pageState === 'closed') {
    const closed = describeClosedOffer(offer.status, sender)

    return (
      <PageRoot>
        <CardWrap>
          <CardSurface padding="loose">
            <Content>
              {brand}
              <Title>{closed.title}</Title>
              <Body>{closed.body}</Body>
            </Content>
          </CardSurface>
        </CardWrap>
      </PageRoot>
    )
  }

  if (pageState === 'accepted') {
    return (
      <PageRoot>
        <CardWrap>
          <CardSurface padding="loose">
            <Content>
              {brand}
              <Title>Thank you, your acceptance is in</Title>
              <Body>
                {sender} has your signed acceptance of {rent} per month from{' '}
                {start} to {end}, and will be in touch to finalize your
                renewal.
              </Body>
            </Content>
          </CardSurface>
        </CardWrap>
      </PageRoot>
    )
  }

  if (pageState === 'declined') {
    return (
      <PageRoot>
        <CardWrap>
          <CardSurface padding="loose">
            <Content>
              {brand}
              <Title>Thanks for letting us know</Title>
              <Body>{sender} has your answer.</Body>
            </Content>
          </CardSurface>
        </CardWrap>
      </PageRoot>
    )
  }

  return (
    <PageRoot>
      <CardWrap>
        <CardSurface padding="loose">
          <Content>
            {brand}
            <Title>Your lease renewal offer</Title>
            <Body>
              {greeting} {sender} would like to renew your lease
              {home === '' ? '' : ` at ${home}`}.
            </Body>

            <DetailList>
              <DetailLabel>New rent</DetailLabel>
              <DetailValue>{rent} per month</DetailValue>
              <DetailLabel>Starts</DetailLabel>
              <DetailValue>{start}</DetailValue>
              <DetailLabel>Ends</DetailLabel>
              <DetailValue>{end}</DetailValue>
            </DetailList>

            {offer.termsText !== '' ? (
              <TermsText>{offer.termsText}</TermsText>
            ) : null}

            {pageState === 'review' ? (
              <ActionRow>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => setPageState('signing')}
                >
                  Accept offer
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setPageState('declining')}
                >
                  Decline
                </Button>
              </ActionRow>
            ) : null}

            {pageState === 'signing' ? (
              <>
                <TextField
                  label="Type your full legal name to sign"
                  placeholder="Your full name"
                  autoComplete="name"
                  value={signerName}
                  onChange={event => setSignerName(event.target.value)}
                />
                <AgreeText>
                  By signing, I agree to renew my lease at {rent} per month from{' '}
                  {start} to {end}, under the terms above.
                </AgreeText>
                <ActionRow>
                  <Button
                    type="button"
                    variant="primary"
                    isLoading={isSubmitting}
                    disabled={isSubmitting || signerName.trim() === ''}
                    onClick={() => void submit('accept')}
                  >
                    Accept and sign
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={isSubmitting}
                    onClick={() => setPageState('review')}
                  >
                    Back
                  </Button>
                </ActionRow>
              </>
            ) : null}

            {pageState === 'declining' ? (
              <>
                <ChoiceTileGroup
                  label="Can you tell us why? (optional)"
                  options={DECLINE_REASON_OPTIONS}
                  selectedValue={declineReason}
                  onChange={setDeclineReason}
                />
                <ActionRow>
                  <Button
                    type="button"
                    variant="primary"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    onClick={() => void submit('decline')}
                  >
                    Decline offer
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={isSubmitting}
                    onClick={() => setPageState('review')}
                  >
                    Back
                  </Button>
                </ActionRow>
              </>
            ) : null}

            {errorMessage !== '' ? <ErrorText>{errorMessage}</ErrorText> : null}
          </Content>
        </CardSurface>
      </CardWrap>
    </PageRoot>
  )
}

export default RenewalOfferPage
